/**
 * @fileoverview Moves a tab that has fallen behind the live build onto it at
 * its next page change ([#6386](https://github.com/CodeinPlace/cip/issues/6386)).
 *
 * A tab keeps the build it loaded until it reloads, and students keep tabs
 * open for days. So the tab asks which build is live when it comes back into
 * view and every {@link LIVE_BUILD_CHECK_INTERVAL_MS} while visible. Once the
 * live build differs from its own, its next change of page loads that page
 * fresh instead of switching in place: the person lands on the live build with
 * no prompt and loses nothing typed, because they were leaving the page.
 *
 * Slides are exempt both ways: a change from or to a deck or a lesson's
 * slides stays in place, since a fresh load there is heavy and may be in front
 * of a class. The tab updates at a later change between other pages.
 *
 * A tab restored from history is handled when it loads, by
 * `restoredBuildCheck.ts`.
 */
import {reloadPage} from 'appReload';
import {fetchLiveBuildCommit} from 'restoredBuildCheck';

/** How often a visible tab asks which build is live. */
export const LIVE_BUILD_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** The least time between two checks, however often the tab is shown. */
export const LIVE_BUILD_CHECK_MIN_GAP_MS = 60 * 1000;

/** `readBuildCommit`'s value for a page with no build stamp. */
const UNKNOWN_BUILD_COMMIT = 'unknown';

/**
 * Pages where a fresh load is never started: anything in the slides app
 * (decks, editing, presenting, following a presenter, shared decks), and a
 * lesson's slides under `learn` or `training`.
 */
const SLIDES_PATH_PATTERNS: readonly RegExp[] = [
  /^\/[^/]+\/slides(\/|$)/,
  /^\/share\/slides(\/|$)/,
  /^\/[^/]+\/(learn|training)\/[^/]+/,
];

/** The part of a data router this module reads. */
export interface PageChangeSource {
  /** The router's current state. */
  state: {location: {pathname: string}};
  /**
   * Calls `listener` after each state change.
   *
   * @return A function that stops the calls.
   */
  subscribe: (listener: (state: {location: {pathname: string}}) => void) => () => void;
}

/**
 * Whether a path shows slides: a deck, or a lesson's slides.
 *
 * @param pathname - A location's pathname.
 * @return True for a slides page.
 * @throws Never.
 */
export function isSlidesPath(pathname: string): boolean {
  return SLIDES_PATH_PATTERNS.some(pattern => pattern.test(pathname));
}

/**
 * Whether the running build is behind the live one.
 *
 * @param params.runningBuild - This page's build stamp.
 * @param params.liveBuild - The live page's stamp, or null when it could not
 *     be read.
 * @return True only when both are known and they differ.
 * @throws Never.
 */
export function isBehindLiveBuild({
  runningBuild,
  liveBuild,
}: {
  runningBuild: string;
  liveBuild: string | null;
}): boolean {
  return (
    runningBuild !== UNKNOWN_BUILD_COMMIT &&
    liveBuild !== null &&
    liveBuild !== runningBuild
  );
}

/**
 * Whether a change of page should be a fresh load.
 *
 * @param params.isBehind - Whether the tab runs an older build than the live
 *     one.
 * @param params.fromPath - The pathname being left.
 * @param params.toPath - The pathname being opened.
 * @return True when the tab is behind, the pathname changed, and neither page
 *     shows slides.
 * @throws Never.
 */
export function shouldLoadPageChange({
  isBehind,
  fromPath,
  toPath,
}: {
  isBehind: boolean;
  fromPath: string;
  toPath: string;
}): boolean {
  return (
    isBehind &&
    fromPath !== toPath &&
    !isSlidesPath(fromPath) &&
    !isSlidesPath(toPath)
  );
}

/**
 * Watches for the live build moving ahead of this tab's, and loads the next
 * page change fresh once it has.
 *
 * @param params.router - The app's data router.
 * @param params.runningBuild - This page's build stamp; `'unknown'` (tests, a
 *     dev page) watches nothing.
 * @param params.fetchLiveBuild - Reads the live build. Omitted → fetches `/`
 *     past the HTTP cache and reads its stamp.
 * @param params.loadPage - Starts the fresh load of the current URL. Omitted →
 *     `reloadPage` with the `stale-build-page-change` reason.
 * @param params.now - The clock, for the gap between checks. Omitted →
 *     `Date.now`.
 * @return A function that stops watching.
 * @throws Never.
 */
export function watchForStaleBuild({
  router,
  runningBuild,
  fetchLiveBuild = fetchLiveBuildCommit,
  loadPage = () => {
    reloadPage({reason: 'stale-build-page-change'});
  },
  now = Date.now,
}: {
  router: PageChangeSource;
  runningBuild: string;
  fetchLiveBuild?: () => Promise<string | null>;
  loadPage?: () => void;
  now?: () => number;
}): () => void {
  if (runningBuild === UNKNOWN_BUILD_COMMIT) {
    return () => {};
  }
  let isBehind = false;
  let lastCheckMs = Number.NEGATIVE_INFINITY;
  let fromPath = router.state.location.pathname;

  const check = async () => {
    if (isBehind || document.visibilityState !== 'visible') {
      return;
    }
    if (now() - lastCheckMs < LIVE_BUILD_CHECK_MIN_GAP_MS) {
      return;
    }
    lastCheckMs = now();
    isBehind = isBehindLiveBuild({
      runningBuild,
      liveBuild: await fetchLiveBuild(),
    });
  };
  const onVisibilityChange = () => {
    void check();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  const interval = window.setInterval(() => {
    void check();
  }, LIVE_BUILD_CHECK_INTERVAL_MS);
  const unsubscribe = router.subscribe(state => {
    const toPath = state.location.pathname;
    if (shouldLoadPageChange({isBehind, fromPath, toPath})) {
      loadPage();
    }
    fromPath = toPath;
  });

  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.clearInterval(interval);
    unsubscribe();
  };
}
